import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { disableReactDevTools } from "@fvilers/disable-react-devtools";

// Components
import { MenuBar } from "./components/MenuBar";
import { DrawingModal } from "./modals/components/DrawingModal";
import { Page } from "./components/Page";
import AlreadySignedComponent from "./components/Common/AlreadySignedComponent";
import Loading from "./components/Common/Loading";
import { AuditTrailModal } from "./modals/components/AuditTrailModal";
import OtpModal from "./modals/components/OtpModal";

// Hooks
import { usePdf, Pdf } from "./hooks/usePdf";
import { useUploader, UploadTypes } from "./hooks/useUploader";
import { useCoordinatesFetcher } from "./hooks/useCoordinatesFetcher";
import { useSigningSubmit } from "./hooks/useSigningSubmit";
import { useSignRejection } from "./hooks/useSignRejection";
import { useFieldNavigation } from "./hooks/useFieldNavigation";
import { useAppInitializer } from "./hooks/useAppInitializer";

// Redux
import { RootState } from "redux/store";

// Styles
import "semantic-ui-css/semantic.min.css";
import "./App.css";

// Disable React DevTools in production
if (process.env.NODE_ENV === "production") {
  disableReactDevTools();
}

const App: React.FC = () => {
  const [drawingModalOpen, setDrawingModalOpen] = useState(false);
  const [isFetchingCordinatesData, setIsFetchingCordinatesData] = useState(true);
  const [isAlreadySign, setIsAlreadySign] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userErrorMsg, setUserErrorMsg] = useState("");
  const [isAuditHistoryShown, setIsAuditHistoryShown] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [mobileView, setMobileView] = useState(window.innerWidth < 595);
  const [isDocumentActionComplete, setIsDocumentActionComplete] = useState(false);

  const signatureIndicatorRef = useRef<any>(null);
  const visitedFieldsRef = useRef<Set<number>>(new Set());

  // Redux state
  const allSignatureData = useSelector((state: RootState) => state.signatureList);
  const allTextData = useSelector((state: RootState) => state.textList.allTextData);
  const allDateData = useSelector((state: RootState) => state.dateList.allDateData);
  const allCheckboxData = useSelector((state: RootState) => state.checkboxList.allCheckboxData);
  const allEmailData = useSelector((state: RootState) => state.emailList.allEmailData);
  const allPicklistData = useSelector((state: RootState) => state.pickList.allPicklistData);
  const coordinatesList = useSelector((state: RootState) => state.coordinatesList);
  const elementsNavigationData = useSelector((state: RootState) => state.elementsNavigationHelper);
  const currentReduxState = useSelector((state: RootState) => state);

  const allCoordinates = React.useMemo(() => [
    ...Object.values(allSignatureData.allSignatureData || {}).flat(),
    ...Object.values(allTextData || {}).flat(),
    ...Object.values(allDateData || {}).flat(),
    ...Object.values(allCheckboxData || {}).flat(),
    ...Object.values(allEmailData || {}).flat(),
    ...Object.values(allPicklistData || {}).flat(),
  ] as any[], [allSignatureData, allTextData, allDateData, allCheckboxData, allEmailData, allPicklistData]);

  // PDF hooks
  const {
    file, initialize, isMultiPage, isFirstPage, isLastPage,
    currentPage, isSaving, setIsSaving, savePdf,
    previousPage, nextPage, setDimensions, name, dimensions, pages, goToPage, pageIndex,
  } = usePdf();

  const initializePageAndAttachments = (pdfDetails: Pdf) => {
    initialize(pdfDetails);
  };

  const { inputRef: pdfInput, handleClick: handlePdfClick, isUploading, onClick, upload: uploadPdf } = useUploader({
    use: UploadTypes.PDF,
    afterUploadPdf: initializePageAndAttachments,
  });

  // Feature hooks
  const { fetchingCordinates } = useCoordinatesFetcher({
    setIsFetchingCordinatesData,
    setIsAlreadySign,
  });

  const { handleSavePdf } = useSigningSubmit({
    coordinatesList,
    signatureList: allSignatureData,
    textList: useSelector((s: RootState) => s.textList),
    dateList: useSelector((s: RootState) => s.dateList),
    checkboxList: useSelector((s: RootState) => s.checkboxList),
    emailList: useSelector((s: RootState) => s.emailList),
    pickList: useSelector((s: RootState) => s.pickList),
    savePdf,
    currentReduxState,
  });

  const { handleSignRejection } = useSignRejection({ setIsSaving, currentReduxState });

  const handleSavePdfAndHideStart = () => {
    const didStartSubmit = handleSavePdf();
    if (didStartSubmit) {
      setIsDocumentActionComplete(true);
    }
  };

  const handleSignRejectionAndHideStart = async (commentText: string) => {
    setIsDocumentActionComplete(true);
    await handleSignRejection(commentText);
  };

  const { handleStartAndScrollElement } = useFieldNavigation({
    allCoordinates,
    elementsNavigationData,
    signatureIndicatorRef,
    visitedFieldsRef,
    viewportHeight,
    pageIndex,
  });

  useAppInitializer({
    setIsAlreadySign,
    setUserErrorMsg,
    setIsLoading,
    fetchingCordinates,
    uploadPdf,
  });

  return (
    <>
      {/* Overlay container for "thank you" / rejection screens */}
      <div id="thankyou-container" />

      {isLoading && <Loading />}

      {isAlreadySign ? (
        <AlreadySignedComponent
          userErrorMsg={userErrorMsg}
          setIsAuditHistoryShown={setIsAuditHistoryShown}
        />
      ) : (
        <>
          <MenuBar
            rejectSign={handleSignRejectionAndHideStart}
            savePdf={handleSavePdfAndHideStart}
            savingPdfStatus={isSaving}
            isPdfLoaded={!!file}
            setIsAuditHistoryShown={setIsAuditHistoryShown}
          />

          <div
            id="pdfViewerContainer"
            className={mobileView ? "pdf-viewer-div-mobile" : "pdf-viewer-div"}
            style={mobileView ? { overflowX: "auto", overflowY: "auto" } : { overflow: "auto" }}
          >
            {!file || isFetchingCordinatesData ? (
              <Loading />
            ) : (
              <div className={mobileView ? "d-flex flex-column align-items-center w-100 flex-grow-1" : "d-flex justify-content-center align-items-center overflow-x-scroll"}>
                <div className="inner-container">
                  {currentPage && (
                    <div className={mobileView ? "position-relative w-100" : "border mb-5 position-relative"}>
                      <Page
                        dimensions={dimensions}
                        updateDimensions={setDimensions}
                        page={currentPage}
                        allPages={pages}
                        goToPage={goToPage}
                        isFetchingCordinatesData={isFetchingCordinatesData}
                        setDrawingModalOpen={setDrawingModalOpen}
                        handleStartAndScrollElement={handleStartAndScrollElement}
                        signatureIndicatorRef={signatureIndicatorRef}
                        updateViewportHeight={(height: number) => setViewportHeight(height)}
                        isDocumentActionComplete={isDocumentActionComplete}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <footer
              className="text-light d-flex justify-content-center justify-content-md-end w-100 mt-auto py-2"
              style={{
                fontSize: "12px",
                backgroundColor: "#354259",
                position: "relative",
                zIndex: 2,
                flexShrink: 0,
              }}
            >
              <div
                className="text-center px-3 fw-bold"
                style={{ cursor: "pointer" }}
                onClick={() => window.open("https://www.eruditeworks.com/", "_blank")}
              >
                Powered By Erudite Docs
              </div>
            </footer>
          </div>

          {drawingModalOpen && (
            <DrawingModal
              open={drawingModalOpen}
              dismiss={() => setDrawingModalOpen(false)}
            />
          )}
        </>
      )}

      {isAuditHistoryShown && (
        <AuditTrailModal setIsAuditHistoryShown={setIsAuditHistoryShown} />
      )}
    </>
  );
};

export default App;
